import React, { Component } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { connect } from "react-redux";
import { getPaymentDetails } from "../../../api/paymentApi";
import {
  getPaymentSummary,
  getProfitSummary,
  getTaxSummary,
} from "../../../api/dashboard";
import RecentPay from "../Payments/ResentPay";
import PageHeading from "../../Shared/PageHeading";
import CountUp from "react-countup";
import TaxReportCards from "../Dashboard/ReportCards/TaxReportCards";
import PaymentReportCards from "../Dashboard/ReportCards/PaymentReportCards";
import CourierReportCards from "../Dashboard/ReportCards/CourierReportCards";
import ProfitLossCards from "../Dashboard/ReportCards/ProfitLossCards";

import MonthlyGstChart from "../Dashboard/ReportCharts/MonthlyGstChart";
import PaymentMethodChart from "../Dashboard/ReportCharts/PaymentMethodChart";
import PaymentSuccessChart from "../Dashboard/ReportCharts/PaymentSuccessChart";
import CourierShipmentChart from "../Dashboard/ReportCharts/CourierShipmentChart";
import DeliveryTimeChart from "../Dashboard/ReportCharts/DeliveryTimeChart";
import RevenueProfitChart from "../Dashboard/ReportCharts/RevenueProfitChart";
import ProfitWaterfallChart from "../Dashboard/ReportCharts/ProfitWaterfallChart";

import {
  courierReportData,
  courierShipmentData,
  deliveryTimeData,
} from "../Dashboard/dashboardReportData";

const toAmount = (...values) => {
  const value = values.find((item) => item !== undefined && item !== null && item !== "");
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const getProfitSummaryViews = (summary = {}) => {
  const cards = {
    revenue: toAmount(summary.revenue, summary.revenueExGst, summary.totalRevenue),
    materialMakingCost: toAmount(
      summary.materialMakingCost,
      summary.productCost,
      summary.materialCost,
    ),
    courierCost: toAmount(summary.courierCost, summary.shippingCost),
    gatewayFees: toAmount(summary.gatewayFees, summary.paymentGatewayFees),
    discountsCoupons: toAmount(summary.discountsCoupons, summary.discounts, summary.discount),
    refunds: toAmount(summary.refunds, summary.refund),
    netProfit: toAmount(summary.netProfit, summary.profit),
    margin: toAmount(summary.margin, summary.marginPercent, summary.profitMargin),
  };

  const monthlyRows =
    summary.monthlyRevenueProfit ||
    summary.revenueProfitData ||
    summary.monthlyData ||
    summary.monthlySummary ||
    summary.monthly ||
    [];
  const revenueProfit = Array.isArray(monthlyRows) && monthlyRows.length
    ? monthlyRows.map((row) => ({
        month: row.month || row.label || row._id || "",
        revenue: toAmount(row.revenue, row.revenueExGst, row.totalRevenue),
        profit: toAmount(row.profit, row.netProfit),
      }))
    : [{
        month: summary.period || "Total",
        revenue: cards.revenue,
        profit: cards.netProfit,
      }];

  const waterfallRows = summary.waterfallData || summary.profitWaterfallData;
  const waterfall = Array.isArray(waterfallRows)
    ? waterfallRows.map((row) => ({
        name: row.name || row.label || "",
        amount: toAmount(row.amount, row.value),
      }))
    : [
        { name: "Revenue", amount: cards.revenue },
        { name: "Material & Making", amount: -cards.materialMakingCost },
        { name: "Courier", amount: -cards.courierCost },
        { name: "Gateway Fees", amount: -cards.gatewayFees },
        { name: "Discounts", amount: -cards.discountsCoupons },
        { name: "Refunds", amount: -cards.refunds },
        { name: "Net Profit", amount: cards.netProfit },
      ];

  return { cards, revenueProfit, waterfall };
};

const getTaxSummaryViews = (response = {}) => {
  const summary =
    response?.data?.taxSummary ||
    response?.taxSummary ||
    response?.data?.totals ||
    response?.totals ||
    response?.data?.summary ||
    response?.summary ||
    response?.data ||
    response;
  const cards = {
    taxableValue: toAmount(
      summary.taxableValue,
      summary.totalTaxableValue,
      summary.taxableAmount,
      summary.totalTaxableAmount,
    ),
    totalGstCollected: toAmount(
      summary.totalGstCollected,
      summary.totalGSTCollected,
      summary.totalGst,
      summary.totalGST,
      summary.totalTaxCollected,
      summary.totalTax,
      summary.total,
    ),
    cgst: toAmount(summary.cgst, summary.totalCgst, summary.cgstAmount, summary.totalCgstAmount),
    sgst: toAmount(summary.sgst, summary.totalSgst, summary.sgstAmount, summary.totalSgstAmount),
    igst: toAmount(summary.igst, summary.totalIgst, summary.igstAmount, summary.totalIgstAmount),
    gstOnShipping: toAmount(
      summary.gstOnShipping,
      summary.shippingGst,
      summary.shippingTax,
      summary.taxOnShipping,
    ),
  };

  const monthlyRows =
    response?.data?.monthlyGst ||
    response?.monthlyGst ||
    response?.data?.monthlyGstData ||
    response?.monthlyGstData ||
    response?.data?.monthlyGST ||
    response?.monthlyGST ||
    response?.data?.monthlyGSTData ||
    response?.monthlyGSTData ||
    response?.data?.monthlyTax ||
    response?.monthlyTax ||
    response?.data?.monthlySummary ||
    response?.monthlySummary ||
    summary.monthlyGst ||
    summary.monthlyGST ||
    summary.monthlyTax ||
    summary.monthlySummary ||
    summary.monthlyData ||
    [];

  const monthlyGst = Array.isArray(monthlyRows)
    ? monthlyRows.map((row) => ({
        month: row.month || row.label || row._id?.month || row._id || "",
        gst: toAmount(
          row.gst,
          row.totalGst,
          row.totalGST,
          row.totalTax,
          row.totalTaxCollected,
          row.gstCollected,
          row.taxAmount,
        ),
      }))
    : [];

  return { cards, monthlyGst };
};

const getPaymentSummaryViews = (response = {}) => {
  const summary =
    response?.data?.paymentSummary ||
    response?.paymentSummary ||
    response?.data?.summary ||
    response?.summary ||
    response?.data ||
    response;

  const successfulPayments = toAmount(
    summary.successfulPayments,
    summary.successful,
    summary.successCount,
    summary.capturedPayments,
  );
  const totalTransactions = toAmount(
    summary.totalTransactions,
    summary.totalPayments,
    summary.transactionCount,
    summary.totalCount,
  );
  const rawSuccessRate = [summary.successRate, summary.paymentSuccessRate].find(
    (value) => value !== undefined && value !== null && value !== "",
  );
  const parsedSuccessRate = Number(String(rawSuccessRate ?? "").replace("%", ""));

  const cards = {
    grossCollected: toAmount(
      summary.grossCollected,
      summary.totalCollected,
      summary.totalAmount,
      summary.totalRevenue,
      summary.grossAmount,
    ),
    gatewayFees: toAmount(
      summary.gatewayFees,
      summary.paymentGatewayFees,
      summary.totalGatewayFees,
      summary.fees,
    ),
    gstOnFees: toAmount(
      summary.gstOnFees,
      summary.gatewayFeeGst,
      summary.feeTax,
      summary.gstOnGatewayFees,
    ),
    refunds: toAmount(summary.refunds, summary.totalRefunds, summary.refundedAmount),
    netSettled: toAmount(
      summary.netSettled,
      summary.netSettlement,
      summary.settledAmount,
      summary.netAmount,
    ),
    pendingSettlement: toAmount(
      summary.pendingSettlement,
      summary.pendingSettlementAmount,
      summary.unsettledAmount,
      summary.pendingAmount,
    ),
    successRate: rawSuccessRate !== undefined && Number.isFinite(parsedSuccessRate)
      ? parsedSuccessRate
      : totalTransactions > 0
        ? (successfulPayments / totalTransactions) * 100
        : 0,
  };

  const methodsSource =
    response?.data?.paymentByMethod ||
    response?.paymentByMethod ||
    response?.data?.paymentMethods ||
    response?.paymentMethods ||
    response?.data?.paymentsByMethod ||
    response?.paymentsByMethod ||
    response?.data?.paymentMethodBreakdown ||
    response?.paymentMethodBreakdown ||
    response?.data?.paymentMethodSummary ||
    response?.paymentMethodSummary ||
    summary.paymentMethods ||
    summary.paymentsByMethod ||
    summary.paymentMethodBreakdown ||
    summary.paymentMethodSummary ||
    [];
  const methods = Array.isArray(methodsSource)
    ? methodsSource
    : Object.entries(methodsSource || {}).map(([name, value]) =>
        typeof value === "object" && value !== null ? { name, ...value } : { name, value },
      );
  const paymentMethods = methods.map((row) => ({
    name: row.name || row.method || row.paymentMethod || row._id || "Other",
    value: toAmount(row.value, row.amount, row.totalAmount, row.total, row.count, row.transactions),
  })).filter((row) => row.value > 0);

  const paymentResultsSource =
    response?.data?.paymentSuccessVsFailed ||
    response?.paymentSuccessVsFailed ||
    summary.paymentSuccessVsFailed;
  if (Array.isArray(paymentResultsSource)) {
    return {
      cards,
      paymentMethods,
      paymentSuccess: paymentResultsSource.map((row) => ({
        name: row.name || row.label || "",
        value: toAmount(row.value, row.count, row.total),
      })),
    };
  }

  const monthlySource =
    response?.data?.monthlyPayments ||
    response?.monthlyPayments ||
    response?.data?.monthlyPaymentSummary ||
    response?.monthlyPaymentSummary ||
    response?.data?.monthlyPaymentStats ||
    response?.monthlyPaymentStats ||
    response?.data?.monthlySuccessFailure ||
    response?.monthlySuccessFailure ||
    response?.data?.paymentTrends ||
    response?.paymentTrends ||
    summary.monthlyPayments ||
    summary.monthlyPaymentSummary ||
    summary.monthlyPaymentStats ||
    summary.monthlySuccessFailure ||
    summary.paymentTrends ||
    summary.monthlyData ||
    [];
  const monthlyRows = Array.isArray(monthlySource) ? monthlySource : [];
  const paymentSuccess = monthlyRows
    .map((row) => ({
      name: row.month || row.label || row._id?.month || row._id || "",
      value: toAmount(
        row.success,
        row.successful,
        row.successfulPayments,
        row.successCount,
        row.capturedPayments,
      ) + toAmount(row.failed, row.failedPayments, row.failedCount, row.failureCount),
    }))
    .filter((row) => row.value > 0);

  if (!paymentSuccess.length && (successfulPayments || summary.failedPayments)) {
    paymentSuccess.push({
      name: "Total",
      value: successfulPayments + toAmount(summary.failedPayments, summary.failed, summary.failedCount),
    });
  }

  return { cards, paymentMethods, paymentSuccess };
};

class Dashboard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      payments: [],
      loading: false,
      paymentSummary: {},
      chartData: [],
      activeReport: null,
      profitSummary: null,
      profitSummaryLoading: false,
      profitSummaryError: "",
      taxSummary: null,
      taxSummaryLoading: false,
      taxSummaryError: "",
      paymentReportSummary: null,
      paymentReportLoading: false,
      paymentReportError: "",
    };
  }

  fetchPaymentDetails = async () => {
    try {
      const response = await getPaymentDetails();

      if (response.success) {
        const data = response.data;

        this.setState({
          paymentSummary: data,
          chartData: [
            {
              name: "Successful",
              value: data.successfulPayments || 0,
            },
            {
              name: "Pending",
              value: data.pendingPayments || 0,
            },
            {
              name: "Failed",
              value: data.failedPayments || 0,
            },
          ],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  fetchProfitSummary = async () => {
    this.setState({ profitSummaryLoading: true, profitSummaryError: "" });

    try {
      const response = await getProfitSummary();
      this.setState({ profitSummary: response?.data?.summary ?? response?.data ?? response });
    } catch (error) {
      this.setState({
        profitSummary: null,
        profitSummaryError:
          error?.response?.data?.message || error.message || "Failed to load profit summary.",
      });
    } finally {
      this.setState({ profitSummaryLoading: false });
    }
  };

  fetchTaxSummary = async () => {
    this.setState({ taxSummaryLoading: true, taxSummaryError: "" });

    try {
      const response = await getTaxSummary();
      this.setState({ taxSummary: response });
    } catch (error) {
      this.setState({
        taxSummary: null,
        taxSummaryError:
          error?.response?.data?.message || error.message || "Failed to load tax summary.",
      });
    } finally {
      this.setState({ taxSummaryLoading: false });
    }
  };

  fetchPaymentSummary = async () => {
    this.setState({ paymentReportLoading: true, paymentReportError: "" });

    try {
      const response = await getPaymentSummary();
      this.setState({ paymentReportSummary: response });
    } catch (error) {
      this.setState({
        paymentReportSummary: null,
        paymentReportError:
          error?.response?.data?.message || error.message || "Failed to load payment summary.",
      });
    } finally {
      this.setState({ paymentReportLoading: false });
    }
  };

  handleReportSelect = (reportId) => {
    this.setState({ activeReport: reportId });

    if (reportId === "tax") {
      this.fetchTaxSummary();
    } else if (reportId === "profit") {
      this.fetchProfitSummary();
    } else if (reportId === "payments") {
      this.fetchPaymentSummary();
    }
  };

  componentDidMount() {
    this.fetchPaymentDetails();
  }

  render() {
    const COLORS = ["#55DD75", "#EAE26C", "#FC8D98"];
    const { fixNavbar } = this.props;
    const profitViews = getProfitSummaryViews(this.state.profitSummary || {});
    const taxViews = getTaxSummaryViews(this.state.taxSummary || {});
    const paymentViews = getPaymentSummaryViews(this.state.paymentReportSummary || {});
    return (
      <>
        <div className={`section-body ${fixNavbar ? "marginTop" : ""}`}>
          <div className="container-fluid np-dashboard-page">
            <div className="row clearfix pt-4">
              <div className="col-6 col-md-4 col-xl-2">
                <div className="card np-dashboard-card">
                  <div className="p-1">
                    <button
                      type="button"
                      className="my_sort_cut text-dark border-0 bg-transparent w-100"
                      onClick={() => this.setState({ activeReport: null })}
                    >
                      <i className="icon-home" aria-hidden="true" />
                      <span>Home</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4 col-xl-2">
                <div
                  className={`card np-dashboard-card np-dashboard-card--recent-pay ${this.state.activeReport === "recentPay" ? "np-dashboard-card--active" : ""}`}
                >
                  <div className="p-1">
                    <button
                      type="button"
                      className="my_sort_cut text-dark border-0 bg-transparent w-100"
                      onClick={() => this.setState({ activeReport: "recentPay" })}
                    >
                      <i className="icon-wallet" /> <span>Recent Pay</span>
                    </button>
                  </div>
                </div>
              </div>

              {[
                { label: "Profit & Loss", id: "profit", icon: "icon-graph" },
                { label: "Tax Report", id: "tax", icon: "icon-calculator" },
                { label: "Payment Report", id: "payments", icon: "icon-credit-card" },
                { label: "Courier Report", id: "courier", icon: "icon-paper-plane" },
              ].map((report) => (
                <div className="col-6 col-md-4 col-xl-2" key={report.id}>
                  <div
                    className={`card np-dashboard-card ${this.state.activeReport === report.id ? "np-dashboard-card--active" : ""}`}
                  >
                    <div className="p-1">
                      <button
                        type="button"
                        className="my_sort_cut text-dark border-0 bg-transparent w-100"
                        onClick={() => this.handleReportSelect(report.id)}
                      >
                        <i className={report.icon} />
                        <span>{report.label}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!this.state.activeReport && (
              <>
                <div className="row clearfix">
                  <div>
                    <h5>Payment Status Overview</h5>
                  </div>

                  <div className="col-md-3">
                    <div className="card np-dashboard-card">
                      <div className="card-body text-center">
                        <h6>Total Transactions</h6>
                        <h3>
                          <CountUp
                            end={this.state.paymentSummary.totalTransactions || 0}
                            duration={2}
                          />
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="card np-dashboard-card">
                      <div className="card-body text-center">
                        <h6>Successful</h6>
                        <h3>
                          <CountUp
                            end={this.state.paymentSummary.successfulPayments || 0}
                            duration={2}
                          />
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="card np-dashboard-card">
                      <div className="card-body text-center">
                        <h6>Pending</h6>
                        <h3>
                          <CountUp
                            end={this.state.paymentSummary.pendingPayments || 0}
                            duration={2}
                          />
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="card np-dashboard-card">
                      <div className="card-body text-center">
                        <h6>Failed</h6>
                        <h3>
                          <CountUp
                            end={this.state.paymentSummary.failedPayments || 0}
                            duration={2}
                          />
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row clearfix mt-4">
                  <div className="col-lg-12">
                    <div className="card">
                      <div className="card-body">
                        <ResponsiveContainer width="100%" height={400}>
                          <PieChart>
                            <Pie
                              data={this.state.chartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={140}
                              fill="#8884d8"
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {this.state.chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>

                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {this.state.activeReport && (
            <div>
              {this.state.activeReport === "recentPay" && <RecentPay />}
              {/* =====================================================
           PROFIT & LOSS
       ====================================================== */}

              {this.state.activeReport === "profit" && (
                <div className="container-fluid mb-4">
                  <PageHeading
                    title="Profit & Loss"
                    className="mb-3"
                    titleClassName="h4 fw-bold mb-0"
                  />

                  {this.state.profitSummaryLoading && <p>Loading profit summary...</p>}
                  {this.state.profitSummaryError && (
                    <p className="text-danger">{this.state.profitSummaryError}</p>
                  )}

                  <div className="row">
                    <ProfitLossCards data={profitViews.cards} />
                  </div>

                  <div className="row">
                    <div className="col-xl-6 col-lg-12 mb-4">
                      <RevenueProfitChart data={profitViews.revenueProfit} />
                    </div>

                    <div className="col-xl-6 col-lg-12 mb-4">
                      <ProfitWaterfallChart data={profitViews.waterfall} />
                    </div>
                  </div>
                </div>
              )}

              {/* =====================================================
           TAX / GST REPORT
       ====================================================== */}

              {this.state.activeReport === "tax" && (
                <div className="container-fluid mb-4">
                  <PageHeading
                    title="Tax (GST) Report"
                    className="mb-3"
                    titleClassName="h4 fw-bold mb-0"
                  />

                  {this.state.taxSummaryLoading && <p>Loading tax summary...</p>}
                  {this.state.taxSummaryError && (
                    <p className="text-danger">{this.state.taxSummaryError}</p>
                  )}
                  {!this.state.taxSummaryLoading && !this.state.taxSummaryError && (
                    <>
                      <div className="row">
                        <TaxReportCards data={taxViews.cards} />
                      </div>
                      <div className="row">
                        <div className="col-12">
                          <MonthlyGstChart data={taxViews.monthlyGst} />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* =====================================================
           PAYMENT GATEWAY REPORT
       ====================================================== */}

              {this.state.activeReport === "payments" && (
                <div className="container-fluid mb-4">
                  <PageHeading
                    title="Payment Gateway Report"
                    className="mb-3"
                    titleClassName="h4 fw-bold mb-0"
                  />

                  {this.state.paymentReportLoading && <p>Loading payment summary...</p>}
                  {this.state.paymentReportError && (
                    <p className="text-danger">{this.state.paymentReportError}</p>
                  )}
                  {!this.state.paymentReportLoading && !this.state.paymentReportError && (
                    <div className="row">
                      <PaymentReportCards data={paymentViews.cards} />
                    </div>
                  )}

                  <div className="row">
                    <div className="col-xl-6 col-lg-12 mb-4">
                      <PaymentMethodChart data={paymentViews.paymentMethods} />
                    </div>

                    <div className="col-xl-6 col-lg-12 mb-4">
                      <PaymentSuccessChart data={paymentViews.paymentSuccess} />
                    </div>
                  </div>
                </div>
              )}
              {/* =====================================================
           COURIER REPORT
       ====================================================== */}

              {this.state.activeReport === "courier" && (
                <div className="container-fluid mb-4">
                  <PageHeading
                    title="Courier Report"
                    className="mb-3"
                    titleClassName="h4 fw-bold mb-0"
                  />

                  <div className="row">
                    <CourierReportCards data={courierReportData} />
                  </div>
                  <div className="row">
                    <div className="col-xl-6 col-lg-12 mb-4">
                      <CourierShipmentChart data={courierShipmentData} />
                    </div>

                    <div className="col-xl-6 col-lg-12 mb-4">
                      <DeliveryTimeChart data={deliveryTimeData} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  }
}
const mapStateToProps = (state) => ({
  fixNavbar: state.settings.isFixNavbar,
});
const mapDispatchToProps = (dispatch) => ({});
export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
